import { TopBar } from '@/layouts/TopBar';
import { JButton } from '@atoms/JButton';
import {
  Box,
  Paper,
  Stack,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useGetCVVideoById } from '../queries/useGetCVVideos';
import Paths from '@/routes/paths';
import { JVideoPlayer } from '@atoms/JVideoPlayer';
import { CV_VIDEO_TABLE_COLS } from '../components/CVVideoList';
import { JTable } from '@atoms/JTable';
import CVVideoRow from './../components/CVVideoRow';
import {
  useEditCVVideoByIdMutation,
  useContentModerationId,
  useSpeechModerationId,
  useTextModerationId,
} from '../mutations/cvVideoMutations';
import CvVideoStatus from '../models/cv-video-status';
import { useCallback, useEffect } from 'react';
import useJSnackbar from '@/hooks/useJSnackbar';
import useJDialog from '@/hooks/useJDialog';
import { useTranslation } from 'react-i18next';
import AcceptanceCvVideoDto from '../models/dto/acceptance-cv-video-dto';
import { TableCellHeader } from '@atoms/JTable/jtable.styled';
import { Loading } from '@atoms/Loading';
import VideoModerationResultDto from '@cv-videos/models/dto/video-moderation-result.dto';
import { DividerWithMarginStyled } from '@styled/divider-with-margin.styled';
import CvVideoAcceptanceDialogBody from '@components/molucules/CvVideoAcceptanceDialogBody';
import CvVideoRejectionDialogBody from '@components/molucules/CvVideoRejectionDialogBody';

export default function CvVideoDetails() {
  const { id } = useParams();
  const { hash } = useLocation();
  const { t } = useTranslation();
  const navigation = useNavigate();
  const { showSnackbar } = useJSnackbar();
  const { showJDialog, closeJDialog } = useJDialog();
  const { data, isLoading: isGetLoading } = useGetCVVideoById(id, !!id);
  const {
    mutate: moderateContentMutation,
    isSuccess: isContentModerationSuccess,
    data: contentModerationData,
    isLoading: isContentModerationLoading,
  } = useContentModerationId();

  const {
    mutate: moderateSpeechMutation,
    isSuccess: isSpeechModerationSuccess,
    data: speechModerationData,
    isLoading: isSpeechModerationLoading,
  } = useSpeechModerationId();

  const {
    mutate: moderateTextMutation,
    isSuccess: isTextModerationSuccess,
    data: textModerationData,
    isLoading: isTextModerationLoading,
  } = useTextModerationId();

  const {
    mutate: editCvVideo,
    isSuccess: isEditSuccess,
    isLoading: isEditLoading,
  } = useEditCVVideoByIdMutation();

  const cvVideo = data?.data;
  const candidate = cvVideo?.candidate;
  const user = candidate?.user;

  const handleReject = useCallback(
    (value: AcceptanceCvVideoDto) => {
      if (!cvVideo?.id) return;
      editCvVideo({
        id: cvVideo?.id,
        payload: value,
      });
    },
    [cvVideo?.id],
  );

  const handleAccept = useCallback(() => {
    if (!cvVideo?.id) return;
    editCvVideo({
      id: cvVideo?.id,
      payload: { status: CvVideoStatus.ACCEPTED },
    });
  }, [cvVideo?.id]);
  const showRejectionDialog = () => {
    showJDialog(
      t('WHY_REJECT'),
      <CvVideoRejectionDialogBody
        candidateName={`${user?.firstname} ${user?.lastname}`}
        onClose={closeJDialog}
        onConfirm={handleReject}
      />,
      'basic',
    );
  };
  const showAcceptanceDialog = () => {
    showJDialog(
      t('WHY_ACCEPT'),
      <CvVideoAcceptanceDialogBody
        candidateName={`${user?.firstname} ${user?.lastname}`}
        onClose={closeJDialog}
        onConfirm={handleAccept}
        cvVideoName={cvVideo?.name}
      />,
      'basic',
    );
  };
  const videoPath = Paths.CvVideos;
  const hashString = hash ? `${hash}` : '';
  const fullPath = `${videoPath}${hashString}`;
  useEffect(() => {
    if (!isEditSuccess) return;
    closeJDialog();
    showSnackbar(t('CV_VIDEO_STATUS_UPDATED'), 'success');

    navigation(fullPath);
  }, [isEditSuccess]);

  useEffect(() => {
    if (!isContentModerationSuccess) return;
    showSnackbar('La modération automatique a réussi', 'success');
  }, [isContentModerationSuccess]);

  useEffect(() => {
    if (!contentModerationData) return;
    showJDialog(
      'Resultat',
      <ModerateResultDialogBody result={contentModerationData.data} />,
      'filter',
    );
  }, [isContentModerationSuccess]);

  useEffect(() => {
    if (!speechModerationData) return;
    showJDialog(
      'Resultat',
      <Box p={3}>
        {speechModerationData.data.map((item) => (
          <Stack direction="row" gap={2} key={item}>
            <div>- {item}</div>
          </Stack>
        ))}
      </Box>,
      'filter',
    );
  }, [isSpeechModerationSuccess]);

  useEffect(() => {
    if (!textModerationData) return;
    showJDialog(
      'Resultat',
      <Box p={3}>
        {textModerationData.data.map((item) => (
          <>
            <Stack direction="row" gap={2} key={item.TextDetection.Id}>
              <div>- {item.Timestamp / 1000}s</div>
              <div> | {item.TextDetection.DetectedText}</div>
              <div> | {item.TextDetection.Type}</div>
              <div> | {item.TextDetection.Confidence.toPrecision(3)}%</div>
            </Stack>
            <DividerWithMarginStyled />
          </>
        ))}
      </Box>,
      'filter',
    );
  }, [isTextModerationSuccess]);

  if (!cvVideo?.id) return <></>;
  const loadingStatus =
    isGetLoading ||
    isEditLoading ||
    isContentModerationLoading ||
    isSpeechModerationLoading ||
    isTextModerationLoading ||
    isContentModerationLoading;

  return (
    <Stack>
      {loadingStatus && <Loading />}
      <TopBar
        navigateToBack={() => navigation(fullPath)}
        title={`CV Vidéo de ${user?.firstname} ${user?.lastname}`}
      >
        <Stack direction="row" gap={2}>
          {cvVideo.status !== CvVideoStatus.REJECTED && (
            <JButton
              data-qa="show-rejection-dialog"
              onClick={showRejectionDialog}
              variant="contained"
              color="error"
            >
              {t('REJECT')}
            </JButton>
          )}
          {cvVideo.status !== CvVideoStatus.ACCEPTED && (
            <JButton
              onClick={showAcceptanceDialog}
              variant="contained"
              color="secondary"
            >
              {t('ACCEPT')}
            </JButton>
          )}
          <JButton
            onClick={() => moderateContentMutation({ id: cvVideo.id })}
            variant="contained"
            color="info"
          >
            {t('VIDEO_CONTENT_MODERATION')}
          </JButton>
          <JButton
            onClick={() => moderateSpeechMutation({ id: cvVideo.id })}
            variant="contained"
            color="info"
          >
            {t('VIDEO_AUDIO_MODERATION')}
          </JButton>
          <JButton
            onClick={() => moderateTextMutation({ id: cvVideo.id })}
            variant="contained"
            color="info"
          >
            {t('VIDEO_TEXT_MODERATION')}
          </JButton>
        </Stack>
      </TopBar>
      <Stack gap={2} p={2} pb={4}>
        {!isGetLoading && cvVideo?.videoUrl && (
          <JVideoPlayer
            videoUrl={cvVideo?.videoUrl}
            thumbnailUrl={cvVideo.thumbnailUrl}
          />
        )}

        <JTable
          columns={CV_VIDEO_TABLE_COLS.map((item) => ({
            ...item,
            orderable: false,
          }))}
        >
          {cvVideo && <CVVideoRow row={cvVideo} single />}
          {cvVideo.status === CvVideoStatus.REJECTED &&
            cvVideo.cvVideoRejections && (
              <>
                <TableCellHeader>
                  {t('REJECTION_REASON')}
                  <Typography variant="body2">
                    <strong>
                      {t(
                        cvVideo.cvVideoRejections[0].reason.toUpperCase() +
                          '_VIDEO',
                      )}{' '}
                    </strong>
                  </Typography>
                </TableCellHeader>
                <TableRow>
                  <Box p={2}>
                    <Typography>
                      {cvVideo.cvVideoRejections[0].comment}
                    </Typography>
                  </Box>
                </TableRow>
              </>
            )}
        </JTable>
      </Stack>
    </Stack>
  );
}

const ModerateResultDialogBody = ({
  result,
}: {
  result: VideoModerationResultDto;
}) => {
  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="right">Nom</TableCell>
            <TableCell align="right">Confiance %</TableCell>
            <TableCell align="right">Temps en secondes</TableCell>
          </TableRow>
        </TableHead>
        {result.ModerationLabels.map((row) => (
          <TableRow
            key={row.Timestamp}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            <TableCell component="th" scope="row">
              {row.ModerationLabel.Name}
            </TableCell>
            <TableCell align="right">
              {row.ModerationLabel.Confidence}
            </TableCell>
            <TableCell align="right">{row.Timestamp}</TableCell>
          </TableRow>
        ))}
      </Table>
    </TableContainer>
  );
};
